import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './meta-capi.api';

import type { MetaCapiEventLogListQuery, SendCapiTestEventRequest } from '@utazzo/shared';

const QK = {
  config: ['meta-capi', 'config'] as const,
  events: (query: MetaCapiEventLogListQuery) => ['meta-capi', 'events', query] as const,
  event: (id: string) => ['meta-capi', 'event', id] as const,
  cohortSummary: ['meta-capi', 'cohort-summary'] as const,
};

export const useMetaCapiConfig = () =>
  useQuery({ queryKey: QK.config, queryFn: api.getMetaCapiConfig, staleTime: 30_000 });

export const useMetaCapiCohortSummary = () =>
  useQuery({
    queryKey: QK.cohortSummary,
    queryFn: api.getMetaCapiCohortSummary,
    staleTime: 15_000,
  });

export const useMetaCapiEvents = (query: MetaCapiEventLogListQuery) =>
  useQuery({
    queryKey: QK.events(query),
    queryFn: () => api.listMetaCapiEvents(query),
    staleTime: 15_000,
  });

export const useMetaCapiEvent = (id: string | null) =>
  useQuery({
    queryKey: QK.event(id ?? ''),
    queryFn: () => api.getMetaCapiEvent(id as string),
    enabled: Boolean(id),
  });

export const useSendMetaCapiTestEvent = () =>
  useMutation({
    mutationFn: (payload: SendCapiTestEventRequest) => api.sendMetaCapiTestEvent(payload),
    onSuccess: (res) => {
      if (res.ok) toast.success('Test event accepted by Meta');
      else
        toast.error(`Meta rejected the test event${res.httpStatus ? ` (${res.httpStatus})` : ''}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to send test event'),
  });

export const useRetryMetaCapiEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.retryMetaCapiEvent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meta-capi', 'events'] });
      toast.success('Delivery re-queued');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to retry delivery'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
